import { Button, Tooltip } from '@mui/material';
import type { ReactNode } from 'react';

/** Tooltip copy shown when a subscribe URL has not been wired yet. */
const COMING_SOON_TOOLTIP = 'subscribe link coming soon';

/**
 * Props for `<SubscribeButton>`.
 */
export interface SubscribeButtonProps {
  /**
   * Human-readable platform name (e.g., `"Apple Podcasts"`,
   * `"RSS feed"`). Used to compose the accessible name
   * `"Subscribe with {label}"` rendered as the button's label and,
   * for disabled buttons, as the `aria-label` on the button so the
   * accessible name is preserved when no `href` is rendered.
   */
  readonly label: string;
  /**
   * Optional destination URL. When present and non-empty, the
   * surface renders as an MUI `<Button>` with `href={url}`,
   * `target="_blank"`, `rel="noopener noreferrer"` so a click opens
   * the platform's subscribe page in a new tab. When absent or
   * empty, the surface renders as a disabled `<Button>` wrapped in
   * a `<Tooltip>` showing "subscribe link coming soon".
   */
  readonly url?: string;
  /**
   * Optional MUI icon node passed through to MUI's
   * `<Button startIcon>` slot. Callers pass platform-specific
   * icons (e.g., `<RssFeedIcon/>`); the component itself does not
   * hard-code icons per platform.
   */
  readonly icon?: ReactNode;
}

/**
 * `<SubscribeButton>` — presentational subscribe surface for the
 * Podcast page (task 9.2, public-website spec).
 *
 * Renders one of two branches per **Requirement 4.2**:
 *
 *   - **Present URL** — MUI `<Button variant="contained">` with
 *     `href={url}`, `target="_blank"`, `rel="noopener noreferrer"`.
 *     The accessible name is `"Subscribe with {label}"`.
 *   - **Absent URL** — disabled MUI `<Button>` wrapped in a
 *     `<Tooltip title="subscribe link coming soon">`. The disabled
 *     button is wrapped in a `<span>` because MUI tooltips require
 *     a ref-holding child (disabled `<button>` elements cannot
 *     receive a ref). The accessible name is preserved via
 *     `aria-label="Subscribe with {label}"` so the disabled surface
 *     still announces the same name to assistive tech.
 *
 * The component is purely presentational — it renders nothing
 * platform-specific. The Podcast page wires the platform-specific
 * label, URL, and icon when mapping over the seven entries listed
 * in **Requirement 4.1**.
 *
 * _Validates: Requirement 4.2 (absent URL → disabled + tooltip;
 * present URL → new-tab link), Requirement 4.1 (used by the Podcast
 * page for each of the seven listed platforms)._
 */
export function SubscribeButton({
  label,
  url,
  icon,
}: SubscribeButtonProps): JSX.Element {
  const accessibleName = `Subscribe with ${label}`;
  if (url !== undefined && url !== '') {
    return (
      <Button
        variant="contained"
        fullWidth
        startIcon={icon}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
      >
        {accessibleName}
      </Button>
    );
  }
  // Tooltip's child must be able to receive a ref; a disabled MUI
  // Button does not, so wrap in a span per the MUI Tooltip docs.
  return (
    <Tooltip title={COMING_SOON_TOOLTIP}>
      <span>
        <Button
          variant="contained"
          fullWidth
          disabled
          startIcon={icon}
          aria-label={accessibleName}
        >
          {accessibleName}
        </Button>
      </span>
    </Tooltip>
  );
}

export default SubscribeButton;
